import React from 'react';

import CustomModal from 'components/CustomModal';
import { Button } from 'components/Button';

import { FolderPath } from 'containers/Documents/components/FolderTreePicker/styles';
import { Description, ModalContent } from 'containers/Documents/components/MoveToFolderModal/styles';

import { OrgUnit, deleteDestinationBlocks, findUnit, parentOf, unitPath } from '../types';

import UnitTreePicker from './UnitTreePicker';
import { listJoin, plural } from './contents';
import { useDialogExpand } from './useUnitTreeView';
import { SectionDescription } from './styles';

export interface DeleteUnitModalProps {
  open: boolean;
  units: OrgUnit[];
  unitId: string | null;
  /** The page tree's expand state, copied when the dialog opens. */
  expandedIds: Set<string>;
  onCancel: () => void;
  onConfirm: (destinationId: string) => Promise<void>;
}

const DeleteUnitModal: React.FunctionComponent<DeleteUnitModalProps> = ({
  open,
  units,
  unitId,
  expandedIds: pageExpandedIds,
  onCancel,
  onConfirm,
}) => {
  const unit = unitId ? findUnit(units, unitId) : null;
  const parentId = unitId ? (parentOf(units, unitId)?.uuid ?? null) : null;
  const blocked = unitId ? deleteDestinationBlocks(units, unitId) : new Map<string, string>();
  const defaultId = parentId && !blocked.has(parentId) ? parentId : null;

  const [destinationId, setDestinationId] = React.useState<string | null>(defaultId);
  const { expandedIds, toggleExpand } = useDialogExpand(open, pageExpandedIds);

  React.useEffect(() => {
    if (open) setDestinationId(defaultId);
    // Only on opening: the default must not overwrite the account owner's choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const children = unit?.children.length ?? 0;
  const people = unit?.userCount ?? 0;
  // Records do not carry a unit yet, so only units and people have to go somewhere.
  const moves = [
    children > 0 ? plural(children, 'unit directly under it', 'units directly under it') : null,
    people > 0 ? plural(people, 'person', 'people') : null,
  ].filter((part): part is string => part !== null);
  const isEmpty = moves.length === 0;
  const destination = destinationId ? findUnit(units, destinationId) : null;

  return (
    <CustomModal
      openModal={open}
      onClose={onCancel}
      modalTitle="Delete unit"
      modalWidth="sm"
      cancelButton={
        <Button onClick={onCancel} variant="outlined" size="large">
          Cancel
        </Button>
      }
      submitButton={
        <Button
          onClick={() => {
            const target = isEmpty ? parentId : destinationId;
            if (target) onConfirm(target);
          }}
          disabled={!isEmpty && !destinationId}
          variant="contained"
          size="large"
          color="error"
        >
          Delete
        </Button>
      }
    >
      {isEmpty ? (
        <ModalContent data-testid="delete-unit-dialog">
          <SectionDescription>
            Delete &quot;{unit?.name}&quot;? It has nothing in it. You can&apos;t undo this.
          </SectionDescription>
        </ModalContent>
      ) : (
        <ModalContent data-testid="delete-unit-dialog">
          <Description>
            {`Everything in "${unit?.name}" needs somewhere to go. It holds ${listJoin(moves)}.`}
            {children > 0 ? ' The units under it keep everything of their own.' : ''}
            {" You can't undo this."}
          </Description>
          <UnitTreePicker
            units={units}
            selectedId={destinationId}
            expandedIds={expandedIds}
            onSelect={setDestinationId}
            onToggleExpand={toggleExpand}
            disabledReasons={blocked}
          />
          <FolderPath>
            {destination ? `They move to ${unitPath(units, destination.uuid)}.` : 'Select where they should go.'}
          </FolderPath>
        </ModalContent>
      )}
    </CustomModal>
  );
};

export default DeleteUnitModal;
